import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import GameCard from '../components/leagues/GameCard';
import CompactGameFilters from '../components/leagues/CompactGameFilters';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getGamePredictions } from '../services/predictions';
import { getEffectiveGameDate } from '../utils/dateUtils';

export default function Scores() {
  const { sport } = useParams();
  const [selectedSport, setSelectedSport] = useState(sport || null); // Will be set by CompactGameFilters
  const [selectedDate, setSelectedDate] = useState(getEffectiveGameDate());
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch games when filters change
  useEffect(() => {
    const fetchGames = async () => {
      // Skip if no sport selected yet
      if (!selectedSport) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await getGamePredictions(selectedSport, selectedDate);

        // Handle different response structures
        if (Array.isArray(data)) {
          setGames(data);
        } else if (data.games && Array.isArray(data.games)) {
          setGames(data.games);
        } else if (data.data && Array.isArray(data.data)) {
          setGames(data.data);
        } else {
          setGames([]);
        }
      } catch (err) {
        console.error('Error fetching games:', err);
        setError('Failed to load games. Please try again.');
        setGames([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [selectedSport, selectedDate]);

  // Update selected sport when route param changes
  useEffect(() => {
    if (sport) {
      setSelectedSport(sport);
    }
  }, [sport]);

  return (
    <div className="space-y-6">
      {/* Compact Filters */}
      <CompactGameFilters
        selectedSport={selectedSport}
        onSportChange={setSelectedSport}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" text="Loading games..." />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="glass-card p-6 text-center">
          <p className="text-danger">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-accent-blue hover:bg-accent-blue/90 text-bg-primary rounded-lg font-semibold transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && games.length === 0 && (
        <div className="glass-card p-12 text-center">
          <div className="text-6xl mb-4">🏈</div>
          <h3 className="text-xl font-semibold text-text-primary mb-2">
            No Games Found
          </h3>
          <p className="text-text-secondary">
            There are no games scheduled for this date.
          </p>
          <p className="text-text-secondary mt-2">
            Try selecting a different date or sport.
          </p>
        </div>
      )}

      {/* Games Grid */}
      {!loading && !error && games.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-text-secondary">
              Showing {games.length} game{games.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.map((game) => (
              <GameCard key={game.id || game.game_id} game={{...game, sport: selectedSport}} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
